import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppHeader from "../../components/AppHeader";
import Loading from "../../components/Loading";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import TuneIcon from "@mui/icons-material/Tune";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useCurrentUser, currencyFormat, resolveFirstListingImage, formatMileage, resolveListingTitle } from "../../utils";
import { useGetAllCarsForSale, useGetAllMakeModelGen, useCatalogMakesModels } from "../../services";
import {
  applyFilters,
  BODY_STYLE_OPTIONS,
  canonicalizeBodyStyle,
  canonicalizeDriveType,
  canonicalizeFuelType,
  canonicalizeTransmission,
  DRIVE_TYPE_OPTIONS,
  FUEL_TYPE_OPTIONS,
  ListingFilters,
  TRANSMISSION_OPTIONS,
} from "../../utils/listingFilter";
import { EXTERIOR_COLORS } from "../../constants/vehicleColors";
import { geocodeZip, LatLng } from "../../utils/distance";
import { formatCityState } from "../../utils/locationDisplay";
import styles from "./SearchResultsPage.module.css";

const MAX_DISTANCE_MI = 500;

const parseOptionalMiles = (s: string): number | null => {
  const t = s.trim();
  if (!t) return null;
  const n = parseFloat(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.min(n, MAX_DISTANCE_MI);
};

const parseOptionalNumber = (s: string): number | null => {
  const t = s.trim();
  if (!t) return null;
  const n = parseFloat(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
};

const uniqueSorted = (values: Array<string | null | undefined>): string[] => {
  const s = new Set<string>();
  values.forEach((v) => {
    if (v?.trim()) s.add(v.trim());
  });
  return Array.from(s).sort((a, b) => a.localeCompare(b));
};

type SortKey = "newest" | "priceAsc" | "priceDesc" | "mileageAsc" | "yearDesc";

type Props = { darkMode: boolean };

const SearchResultsPage: React.FC<Props> = ({ darkMode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, error, loading, refetch } = useGetAllCarsForSale();
  const { data: makeModelData } = useGetAllMakeModelGen();
  const { makes: catalogMakes, modelsFor: catalogModelsFor } = useCatalogMakesModels();
  const [refetching, setRefetching] = useState(false);
  const user = useCurrentUser();
  const userId = user?.id;

  // ------- State hydrated from URL on first render, then kept in sync -------
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [selectedMake, setSelectedMake] = useState<string | null>(searchParams.get("make") || null);
  const [selectedModel, setSelectedModel] = useState<string | null>(searchParams.get("model") || null);
  const [minYear, setMinYear] = useState<string>(searchParams.get("minYear") || "");
  const [maxYear, setMaxYear] = useState<string>(searchParams.get("maxYear") || "");
  const [minPrice, setMinPrice] = useState<string>(searchParams.get("minPrice") || "");
  const [maxPrice, setMaxPrice] = useState<string>(searchParams.get("maxPrice") || "");
  const [minMileage, setMinMileage] = useState<string>(searchParams.get("minMileage") || "");
  const [maxMileage, setMaxMileage] = useState<string>(searchParams.get("maxMileage") || "");
  const [selectedBody, setSelectedBody] = useState<string | null>(searchParams.get("body") || null);
  const [selectedColor, setSelectedColor] = useState<string | null>(searchParams.get("color") || null);
  const [selectedTrans, setSelectedTrans] = useState<string | null>(searchParams.get("trans") || null);
  const [selectedFuel, setSelectedFuel] = useState<string | null>(searchParams.get("fuel") || null);
  const [selectedDrive, setSelectedDrive] = useState<string | null>(searchParams.get("drive") || null);
  const [zipCode, setZipCode] = useState<string>(searchParams.get("zip") || "");
  const [maxDistance, setMaxDistance] = useState<string>(() => {
    const legacy = searchParams.get("distance");
    const dMax = searchParams.get("distMax");
    return dMax || legacy || "";
  });
  const [sortKey, setSortKey] = useState<SortKey>("newest");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // ------- Keep URL in sync when state changes -------
  useEffect(() => {
    const next = new URLSearchParams();
    if (searchQuery) next.set("q", searchQuery);
    if (selectedMake) next.set("make", selectedMake);
    if (selectedModel) next.set("model", selectedModel);
    if (minYear) next.set("minYear", minYear);
    if (maxYear) next.set("maxYear", maxYear);
    if (minPrice) next.set("minPrice", minPrice);
    if (maxPrice) next.set("maxPrice", maxPrice);
    if (minMileage) next.set("minMileage", minMileage);
    if (maxMileage) next.set("maxMileage", maxMileage);
    if (selectedBody) next.set("body", selectedBody);
    if (selectedColor) next.set("color", selectedColor);
    if (selectedTrans) next.set("trans", selectedTrans);
    if (selectedFuel) next.set("fuel", selectedFuel);
    if (selectedDrive) next.set("drive", selectedDrive);
    if (zipCode) next.set("zip", zipCode);
    if (maxDistance.trim()) next.set("distMax", maxDistance.trim());
    setSearchParams(next, { replace: true });
    // setSearchParams is stable from react-router; omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchQuery, selectedMake, selectedModel, minYear, maxYear, minPrice, maxPrice,
    minMileage, maxMileage, selectedBody, selectedColor, selectedTrans, selectedFuel,
    selectedDrive, zipCode, maxDistance,
  ]);

  // ------- ZIP → lat/lng for distance filter (Google when loaded, else Zippopotam) -------
  const [userLocation, setUserLocation] = useState<LatLng | null>(null);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const [zipResolving, setZipResolving] = useState(false);

  useEffect(() => {
    if (!/^\d{5}$/.test(zipCode)) {
      setUserLocation(null);
      setGeocodeError(null);
      setZipResolving(false);
      return;
    }
    let cancelled = false;
    setZipResolving(true);
    const timer = setTimeout(async () => {
      const loc = await geocodeZip(zipCode);
      if (cancelled) return;
      setZipResolving(false);
      if (loc) {
        setUserLocation(loc);
        setGeocodeError(null);
      } else {
        setUserLocation(null);
        setGeocodeError("Couldn't find that zip code");
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      setZipResolving(false);
    };
  }, [zipCode]);

  // ------- Derived: filtered + sorted cars -------
  const allCars = useMemo(() => {
    return (
      data?.allCarsForSales?.nodes?.filter(
        (car): car is NonNullable<typeof car> =>
          car != null && (!userId || car.sellerId !== userId)
      ) || []
    );
  }, [data, userId]);

  const filters: ListingFilters = useMemo(() => {
    const parsedMax = parseOptionalMiles(maxDistance);
    const useDist = Boolean(userLocation && parsedMax != null);
    return {
      query: searchQuery,
      make: selectedMake,
      model: selectedModel,
      minYear: minYear ? parseInt(minYear, 10) : null,
      maxYear: maxYear ? parseInt(maxYear, 10) : null,
      minPrice: minPrice ? parseFloat(minPrice) : null,
      maxPrice: maxPrice ? parseFloat(maxPrice) : null,
      minMileage: parseOptionalNumber(minMileage),
      maxMileage: parseOptionalNumber(maxMileage),
      bodyStyle: selectedBody,
      color: selectedColor,
      transmission: selectedTrans,
      fuelType: selectedFuel,
      driveType: selectedDrive,
      userLocation,
      distanceMaxMiles: useDist ? parsedMax : null,
    };
  }, [
    searchQuery, selectedMake, selectedModel, minYear, maxYear, minPrice, maxPrice,
    minMileage, maxMileage, selectedBody, selectedColor, selectedTrans, selectedFuel,
    selectedDrive, userLocation, maxDistance,
  ]);

  const filteredCars = useMemo(() => applyFilters(allCars, filters), [allCars, filters]);

  const sortedCars = useMemo(() => {
    const arr = [...filteredCars];
    const priceOf = (c: any) => parseFloat(c.price?.toString() ?? "0");
    const dateOf = (c: any) => (c.createdAt ? new Date(c.createdAt).getTime() : 0);
    switch (sortKey) {
      case "priceAsc":  arr.sort((a, b) => priceOf(a) - priceOf(b)); break;
      case "priceDesc": arr.sort((a, b) => priceOf(b) - priceOf(a)); break;
      case "mileageAsc":arr.sort((a, b) => (a.mileage ?? Infinity) - (b.mileage ?? Infinity)); break;
      case "yearDesc":  arr.sort((a, b) => (b.year ?? 0) - (a.year ?? 0)); break;
      case "newest":
      default:          arr.sort((a, b) => dateOf(b) - dateOf(a)); break;
    }
    return arr;
  }, [filteredCars, sortKey]);

  // Dropdown options come from the curated popular_makes / car_model tables so
  // users can always filter by any supported make/model, regardless of whether
  // a matching listing currently exists. We then union in any value that
  // actually appears in listings to cover custom entries.
  const availableMakes = useMemo(() => {
    const s = new Set<string>();
    catalogMakes.forEach(m => s.add(m));
    makeModelData?.allPopularMakes?.nodes?.forEach(node => {
      if (node?.name) s.add(node.name);
    });
    allCars.forEach(c => { if (c.make) s.add(c.make); });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [catalogMakes, makeModelData, allCars]);

  const availableModels = useMemo(() => {
    if (!selectedMake) return [];
    const s = new Set<string>();
    catalogModelsFor(selectedMake).forEach(m => s.add(m));
    const curatedMake = makeModelData?.allPopularMakes?.nodes?.find(
      node => node?.name?.toLowerCase() === selectedMake.toLowerCase()
    );
    curatedMake?.carModelsByIdPopularMake?.nodes?.forEach(m => {
      if (m?.name) s.add(m.name);
    });
    allCars.forEach(c => {
      if (!c.model) return;
      if (c.make?.toLowerCase() !== selectedMake.toLowerCase()) return;
      s.add(c.model);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [catalogModelsFor, makeModelData, allCars, selectedMake]);

  const availableBodies = useMemo(() => {
    const fromListings = allCars
      .map((c) => canonicalizeBodyStyle(c.bodyStyle))
      .filter((v): v is string => Boolean(v));
    return uniqueSorted([...BODY_STYLE_OPTIONS, ...fromListings]);
  }, [allCars]);

  const availableColors = useMemo(() => {
    const fromListings = allCars.map((c) => c.exteriorColor);
    return uniqueSorted([...EXTERIOR_COLORS.filter((c) => c !== "Other"), ...fromListings]);
  }, [allCars]);

  const availableTransmissions = useMemo(() => {
    const fromListings = allCars
      .map((c) => canonicalizeTransmission(c.transmission))
      .filter((v): v is string => Boolean(v));
    return uniqueSorted([...TRANSMISSION_OPTIONS, ...fromListings]);
  }, [allCars]);

  const availableFuels = useMemo(() => {
    const fromListings = allCars
      .map((c) => canonicalizeFuelType(c.fuelType))
      .filter((v): v is string => Boolean(v));
    return uniqueSorted([...FUEL_TYPE_OPTIONS, ...fromListings]);
  }, [allCars]);

  const availableDrives = useMemo(() => {
    const fromListings = allCars
      .map((c) => canonicalizeDriveType(c.driveType))
      .filter((v): v is string => Boolean(v));
    return uniqueSorted([...DRIVE_TYPE_OPTIONS, ...fromListings]);
  }, [allCars]);

  // ------- Actions -------
  const clearAll = useCallback(() => {
    setSearchQuery("");
    setSelectedMake(null);
    setSelectedModel(null);
    setMinYear("");
    setMaxYear("");
    setMinPrice("");
    setMaxPrice("");
    setMinMileage("");
    setMaxMileage("");
    setSelectedBody(null);
    setSelectedColor(null);
    setSelectedTrans(null);
    setSelectedFuel(null);
    setSelectedDrive(null);
    setZipCode("");
    setMaxDistance("");
  }, []);

  const hasActiveFilters = Boolean(
    searchQuery || selectedMake || selectedModel ||
    minYear || maxYear || minPrice || maxPrice ||
    minMileage || maxMileage || selectedBody || selectedColor ||
    selectedTrans || selectedFuel || selectedDrive ||
    (userLocation && maxDistance.trim() !== "")
  );

  const handleRefresh = useCallback(async () => {
    setRefetching(true);
    try {
      await refetch();
    } finally {
      setRefetching(false);
    }
  }, [refetch]);

  if (loading) {
    return (
      <div className={`${styles.pageContainer} ${darkMode ? styles.dark : ""}`}>
        <AppHeader />
        <Loading />
      </div>
    );
  }
  if (error) console.error("Error loading listings:", error);

  return (
    <div className={`${styles.pageContainer} ${darkMode ? styles.dark : ""}`}>
      <AppHeader />

      <div className={styles.topBar}>
        <div className={styles.topBarInner}>
          <div className={styles.searchBox}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder='Try "Lexus RX", "2022 Toyota", or just a model...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            type="button"
            className={styles.mobileFiltersToggle}
            onClick={() => setMobileFiltersOpen(v => !v)}
          >
            <TuneIcon /> Filters
          </button>
        </div>
      </div>

      <div className={styles.layout}>
        <div
          className={`${styles.sidebarStickyWrap} ${!mobileFiltersOpen ? styles.sidebarHidden : ""}`}
        >
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <h3 className={styles.sidebarTitle}>Filters</h3>
            <button
              className={styles.clearAll}
              onClick={clearAll}
              disabled={!hasActiveFilters}
              type="button"
            >
              Clear all
            </button>
          </div>

          {/* Live match count — primary feedback for filter changes, sits right
              next to the filter controls so the user can watch it update. */}
          <div className={styles.matchSummary} aria-live="polite">
            <div className={styles.matchCountValue}>{sortedCars.length}</div>
            <div className={styles.matchCountLabel}>
              {sortedCars.length === 1 ? "vehicle matches" : "vehicles match"}
              {hasActiveFilters ? " your filters" : ""}
            </div>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel} htmlFor="flt-make">Make</label>
            <select
              id="flt-make"
              data-testid="filter-make"
              className={styles.select}
              value={selectedMake || ""}
              onChange={(e) => {
                setSelectedMake(e.target.value || null);
                setSelectedModel(null);
              }}
            >
              <option value="">Any make</option>
              {availableMakes.map(m => (<option key={m} value={m}>{m}</option>))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel} htmlFor="flt-model">Model</label>
            <select
              id="flt-model"
              data-testid="filter-model"
              className={styles.select}
              value={selectedModel || ""}
              onChange={(e) => setSelectedModel(e.target.value || null)}
              disabled={!selectedMake}
            >
              <option value="">{selectedMake ? "Any model" : "Select a make first"}</option>
              {availableModels.map(m => (<option key={m} value={m}>{m}</option>))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel} htmlFor="flt-body">Body style</label>
            <select
              id="flt-body"
              data-testid="filter-body"
              className={styles.select}
              value={selectedBody || ""}
              onChange={(e) => setSelectedBody(e.target.value || null)}
            >
              <option value="">Any body style</option>
              {availableBodies.map(m => (<option key={m} value={m}>{m}</option>))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Year</label>
            <div className={styles.rangeRow}>
              <input type="number" inputMode="numeric" className={styles.input}
                placeholder="Min" min={1900} max={2100}
                value={minYear} onChange={(e) => setMinYear(e.target.value)} />
              <span className={styles.rangeDash}>-</span>
              <input type="number" inputMode="numeric" className={styles.input}
                placeholder="Max" min={1900} max={2100}
                value={maxYear} onChange={(e) => setMaxYear(e.target.value)} />
            </div>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Price</label>
            <div className={styles.rangeRow}>
              <input type="number" inputMode="numeric" className={styles.input}
                placeholder="Min $" min={0} data-testid="filter-min-price"
                value={minPrice} onChange={(e) => setMinPrice(e.target.value)} />
              <span className={styles.rangeDash}>-</span>
              <input type="number" inputMode="numeric" className={styles.input}
                placeholder="Max $" min={0} data-testid="filter-max-price"
                value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
            </div>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Mileage</label>
            <div className={styles.rangeRow}>
              <input type="number" inputMode="numeric" className={styles.input}
                placeholder="Min mi" min={0} data-testid="filter-min-mileage"
                value={minMileage} onChange={(e) => setMinMileage(e.target.value)} />
              <span className={styles.rangeDash}>-</span>
              <input type="number" inputMode="numeric" className={styles.input}
                placeholder="Max mi" min={0} data-testid="filter-max-mileage"
                value={maxMileage} onChange={(e) => setMaxMileage(e.target.value)} />
            </div>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel} htmlFor="flt-color">Color</label>
            <select
              id="flt-color"
              data-testid="filter-color"
              className={styles.select}
              value={selectedColor || ""}
              onChange={(e) => setSelectedColor(e.target.value || null)}
            >
              <option value="">Any color</option>
              {availableColors.map(m => (<option key={m} value={m}>{m}</option>))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel} htmlFor="flt-trans">Transmission</label>
            <select
              id="flt-trans"
              data-testid="filter-transmission"
              className={styles.select}
              value={selectedTrans || ""}
              onChange={(e) => setSelectedTrans(e.target.value || null)}
            >
              <option value="">Any transmission</option>
              {availableTransmissions.map(m => (<option key={m} value={m}>{m}</option>))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel} htmlFor="flt-fuel">Fuel</label>
            <select
              id="flt-fuel"
              data-testid="filter-fuel"
              className={styles.select}
              value={selectedFuel || ""}
              onChange={(e) => setSelectedFuel(e.target.value || null)}
            >
              <option value="">Any fuel</option>
              {availableFuels.map(m => (<option key={m} value={m}>{m}</option>))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel} htmlFor="flt-drive">Drivetrain</label>
            <select
              id="flt-drive"
              data-testid="filter-drive"
              className={styles.select}
              value={selectedDrive || ""}
              onChange={(e) => setSelectedDrive(e.target.value || null)}
            >
              <option value="">Any drivetrain</option>
              {availableDrives.map(m => (<option key={m} value={m}>{m}</option>))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel} htmlFor="flt-zip">
              Distance from ZIP
            </label>
            <div className={styles.distanceControls}>
              <input
                id="flt-zip"
                type="text"
                inputMode="numeric"
                pattern="\d{5}"
                maxLength={5}
                placeholder="Zip code"
                className={styles.input}
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
              />
              <input
                type="number"
                inputMode="decimal"
                min={0}
                max={MAX_DISTANCE_MI}
                step={1}
                placeholder={`Max distance (mi)`}
                className={styles.input}
                value={maxDistance}
                onChange={(e) => setMaxDistance(e.target.value)}
                disabled={!userLocation}
                aria-label="Maximum distance in miles"
              />
              {zipCode.length > 0 && zipCode.length < 5 && (
                <div className={styles.helpText}>Enter a 5-digit zip code</div>
              )}
              {geocodeError && <div className={styles.errorText}>{geocodeError}</div>}
              {zipResolving && (
                <div className={styles.helpText}>Looking up location…</div>
              )}
              {!userLocation && (
                <div className={styles.helpText}>Enter a zip to enable the distance filter</div>
              )}
              {userLocation && (
                <div className={styles.helpText}>
                  Leave empty to ignore distance (up to {MAX_DISTANCE_MI} mi).
                </div>
              )}
            </div>
          </div>

          {/* Footer actions. Filters apply instantly, but an explicit Apply /
              Refresh button lets the user re-query the server (e.g. to pick up
              brand-new listings) and gives confirmatory feedback. */}
          <div className={styles.sidebarFooter}>
            <button
              type="button"
              className={styles.applyButton}
              onClick={handleRefresh}
              disabled={refetching}
            >
              <RefreshIcon className={refetching ? styles.spinning : undefined} />
              {refetching ? "Refreshing…" : "Apply & refresh"}
            </button>
          </div>
        </aside>
        </div>

        <section className={styles.results}>
          <div className={styles.resultsHeader}>
            <div>
              <div className={styles.resultsCount}>
                {sortedCars.length} {sortedCars.length === 1 ? "vehicle" : "vehicles"}
              </div>
              <div className={styles.resultsHint}>
                {hasActiveFilters ? "Matching your filters" : "All listings"}
              </div>
            </div>
            <div className={styles.sortControl}>
              <label htmlFor="sort">Sort by</label>
              <select
                id="sort"
                className={styles.sortSelect}
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
              >
                <option value="newest">Newest</option>
                <option value="priceAsc">Price: low to high</option>
                <option value="priceDesc">Price: high to low</option>
                <option value="mileageAsc">Lowest mileage</option>
                <option value="yearDesc">Newest model year</option>
              </select>
            </div>
          </div>

          <div className={styles.grid}>
            {sortedCars.length === 0 ? (
              <div className={styles.noResults}>
                <DirectionsCarIcon className={styles.noResultsIcon} />
                <h3>No vehicles found</h3>
                <p>Try broadening your filters or clearing some of them.</p>
                <button
                  className={styles.clearAll}
                  onClick={clearAll}
                  type="button"
                  disabled={!hasActiveFilters}
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              sortedCars.map(car => {
                const location = formatCityState((car as any).locationAddress);
                return (
                  <Link to={`/product/${car.id}`} key={car.id} className={styles.card} data-testid="listing-card" data-listing-id={car.id}>
                    <div className={styles.cardImage}>
                      <img
                        src={resolveFirstListingImage(car.imageLoc, { darkMode })}
                        alt={resolveListingTitle(car)}
                      />
                    </div>
                    <div className={styles.cardContent}>
                      <div className={styles.cardPrice}>
                        {currencyFormat(parseFloat(car.price?.toString() ?? "0"))}
                      </div>
                      <h3 className={styles.cardTitle}>
                        {resolveListingTitle(car)}
                      </h3>
                      <div className={styles.cardMeta}>
                        <span>{formatMileage(car.mileage?.toString())}</span>
                        {location && (
                          <span className={styles.cardLocation}>
                            <LocationOnIcon /> {location}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default SearchResultsPage;
